import { useTranslation } from 'react-i18next'
import { useDiagnosticReport } from '@/hooks/admin/useDiagnosticReport'

/**
 * 원생 상세 진단 탭의 "추천 과정" 섹션 (spec F1).
 * 진단 리포트(GET /lc/admin/diagnostics/{id}/report)의 recommendedCourse + level(총점/밴드)을 표시.
 * fail-soft: 403(ENTRANCE_TEST 미보유)/네트워크 에러 시 렌더 없음.
 * 안내 케이스: 미배정/미채점(데이터 null) · needsAiq(수학머리 미응시) · course=null(과정 미배정·학습불가).
 */
export function TestReportRecommendedCourse({ learnerId }: { learnerId: number }) {
  const { t } = useTranslation('admin')
  const { data, isLoading, isError } = useDiagnosticReport(learnerId)

  // fail-soft: 403 등 에러 시 렌더 없음
  if (isError) return null

  return (
    <section data-testid="recommended-course" className="rounded border border-gray-200 p-4">
      <h2 className="text-sm font-bold text-gray-900">{t('students.detail.diagnostic.recommendedCourse.title')}</h2>
      {isLoading ? (
        <p role="status" className="mt-2 text-xs text-gray-400">
          {t('students.detail.diagnostic.recommendedCourse.loading')}
        </p>
      ) : !data ? (
        <p data-testid="recommended-course-none" className="mt-2 text-xs text-gray-400">
          {t('students.detail.diagnostic.recommendedCourse.notCompleted')}
        </p>
      ) : (
        <RecommendedCourseBody data={data} />
      )}
    </section>
  )
}

function RecommendedCourseBody({
  data,
}: {
  data: NonNullable<ReturnType<typeof useDiagnosticReport>['data']>
}) {
  const { t } = useTranslation('admin')
  const rec = data.recommendedCourse
  const level = data.level

  return (
    <div className="mt-2 space-y-2">
      <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1 text-sm">
        {rec?.course ? (
          <span data-testid="recommended-course-value" className="text-lg font-bold text-primary-700">
            {t('students.detail.diagnostic.recommendedCourse.course', { course: rec.course })}
          </span>
        ) : (
          <span data-testid="recommended-course-empty" className="text-sm font-medium text-gray-500">
            {t('students.detail.diagnostic.recommendedCourse.noCourse')}
          </span>
        )}
        {level && (
          <span className="text-xs text-gray-500">
            {t('students.detail.diagnostic.recommendedCourse.levelSummary', {
              score: level.totalScore,
              band: t(`students.detail.diagnostic.recommendedCourse.band.${level.band}`),
            })}
          </span>
        )}
      </div>
      {rec?.needsAiq && (
        <p data-testid="recommended-course-needs-aiq" className="text-xs text-amber-600">
          {t('students.detail.diagnostic.recommendedCourse.needsAiq')}
        </p>
      )}
    </div>
  )
}
